import type { Event } from '@sentry/react';

/**
 * Tira query string e fragmento das URLs que vão ao Sentry. O checkout recebe `?access_token=`,
 * `?api_key=`, `?token=` (troca de cartão) e UTMs; a URL da página, o Referer, as migalhas de
 * navegação/fetch/xhr e as mensagens levavam isso em claro. O caminho fica — é o que diz a página.
 */

/** `https://x/p/a?access_token=…#y` → `https://x/p/a`. */
export function stripQuery(url: string): string {
	return url.replace(/[?#].*$/, '');
}

// URL absoluta ou caminho (`/p/a?x=1`) dentro de um texto: corta do `?`/`#` até o fim da URL.
const URL_QUERY_IN_TEXT = /((?:https?:\/\/|\/)[^\s?#"'<>]*)[?#][^\s"'<>]*/g;

/** Mesma coisa para URLs soltas num texto (mensagem de erro, de console, de migalha). */
export function stripQueryInText(text: string): string {
	return text.replace(URL_QUERY_IN_TEXT, '$1');
}

const URL_KEYS = ['url', 'from', 'to'] as const;

export function scrubEventUrls<E extends Event>(event: E): E {
	if (event.request) {
		const request = { ...event.request };
		delete request.query_string;
		if (typeof request.url === 'string')
			request.url = stripQuery(request.url);
		if (request.headers) {
			const headers = { ...request.headers };
			for (const key of Object.keys(headers)) {
				if (/^referer$/i.test(key))
					headers[key] = stripQuery(headers[key]);
			}
			request.headers = headers;
		}
		event = { ...event, request };
	}

	if (event.breadcrumbs) {
		event = {
			...event,
			breadcrumbs: event.breadcrumbs.map((crumb) => {
				const out = { ...crumb };
				if (typeof out.message === 'string') {
					out.message = stripQueryInText(out.message);
				}
				if (out.data) {
					const data = { ...out.data };
					for (const key of URL_KEYS) {
						if (typeof data[key] === 'string') {
							data[key] = stripQuery(data[key]);
						}
					}
					out.data = data;
				}
				return out;
			})
		};
	}

	if (typeof event.message === 'string') {
		event = { ...event, message: stripQueryInText(event.message) };
	}

	if (event.exception?.values) {
		event = {
			...event,
			exception: {
				...event.exception,
				values: event.exception.values.map((value) =>
					typeof value.value === 'string'
						? { ...value, value: stripQueryInText(value.value) }
						: value
				)
			}
		};
	}

	return event;
}
